import { useCallback } from 'react';

import { UploadModal } from '@/components/modals/upload-modal';
import { useModal } from './modal-context';

/**
 * Hook returning an imperative `openUploadModal({ engagementId, projectId })`.
 *
 * The documents library calls this from its "Upload" button and from the
 * drag-drop handler attached to the route root — both paths land here
 * so the modal opens with the same engagement context regardless of how
 * the user surfaced it.
 *
 * The hook owns the modal lifecycle via the global modal context (single
 * modal at a time, ESC + click-outside dismiss). Callers don't need to
 * track open / closed state themselves. The modal owns the "Whose document
 * is this?" choice (T-0215); callers pass no party.
 */
export interface OpenUploadModalOptions {
  engagementId: string;
  projectId: string;
}

export function useUploadModal() {
  const { openModal } = useModal();
  return useCallback(
    (opts: OpenUploadModalOptions) => {
      openModal(({ dismiss }) => (
        <UploadModal
          engagementId={opts.engagementId}
          projectId={opts.projectId}
          open={true}
          onOpenChange={(o) => {
            if (!o) dismiss();
          }}
        />
      ));
    },
    [openModal],
  );
}
